import { useState } from "react";
import { Link } from "react-router-dom";
import { FileText, ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { CertificateRequestDialog } from "./CertificateRequestDialog";
import { BrochureButton } from "./BrochureButton";
import { WORKS, WORK_FILTERS } from "../../data/site";
import { projectForWork } from "../../data/projects";

const WorkActions = ({ w, onRequest }) => {
  const project = projectForWork(w.id);
  return (
    <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2">
      <button type="button" onClick={() => onRequest(w)} data-testid={`work-request-cert-${w.id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--rust)] hover:underline">
        <FileText className="h-4 w-4" /> Request certificate
      </button>
      {project && (
        <Link to={`/projects/${project.slug}`} data-testid={`work-project-link-${w.id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--ink)] hover:underline">
          View project <ArrowUpRight className="h-4 w-4" />
        </Link>
      )}
    </div>
  );
};

export const Works = () => {
  const [filter, setFilter] = useState("all");
  const [certWork, setCertWork] = useState(null);
  const list = WORKS.filter((w) => filter === "all" || w.c === filter);

  return (
    <section id="works" data-testid="works-section" className="section bg-[var(--paper)]">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <Reveal>
          <Eyebrow>Works register</Eyebrow>
          <h2 className="section-title mt-4">Completed works</h2>
          <p className="mt-4 max-w-2xl text-[var(--muted-ink)]">Values are as stated on each work done certificate issued by the department. Newest first.</p>
        </Reveal>
        <div className="mt-10 flex flex-wrap gap-2" role="tablist">
          {WORK_FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              data-testid={`works-filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`border px-4 py-2 text-sm font-semibold transition-colors ${filter === f.id ? "border-[var(--ink)] bg-[var(--ink)] text-white" : "border-black/20 text-[var(--ink)] hover:border-[var(--ink)]"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <ul data-testid="works-list" className="mt-8 border-t-2 border-[var(--ink)]">
          {list.map((w) => (
            <li key={w.title} data-testid="work-item" className="work-row grid gap-4 border-b border-black/10 py-7 md:grid-cols-12 md:gap-8">
              <span className="font-mono text-sm text-[var(--rust)] md:col-span-2">{w.yr}</span>
              <div className="md:col-span-7">
                <h3 className="text-lg font-semibold leading-snug text-[var(--ink)]">{w.title}</h3>
                <p className="mt-1 text-sm text-[var(--muted-ink)]">{w.dept}</p>
                {w.qty.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {w.qty.map((q) => <span key={q} className="bg-[var(--sand)] px-2 py-1 font-mono text-xs text-[var(--ink)]">{q}</span>)}
                  </div>
                )}
                <WorkActions w={w} onRequest={setCertWork} />
              </div>
              <div className="md:col-span-3 md:text-right">
                <p className="font-display text-3xl font-extrabold text-[var(--ink)]">{w.val}</p>
                <p className="font-mono text-xs uppercase tracking-wider text-[var(--muted-ink)]">{w.note}</p>
              </div>
            </li>
          ))}
        </ul>
        <div className="mt-8 flex flex-col justify-between gap-4 md:flex-row md:items-center">
          <p className="text-sm text-[var(--muted-ink)]">Work done certificates are available on request. Use "Request certificate" on any work.</p>
          <BrochureButton dark testid="works-brochure-button" />
        </div>
        <CertificateRequestDialog work={certWork} onClose={() => setCertWork(null)} />
      </div>
    </section>
  );
};
